import { PieChart, Pie, Cell } from "recharts";
import type { PieLabelRenderProps } from "recharts";
import { Empty, theme } from "antd";
import { useTranslation } from "react-i18next";
import type { ClaimByStatus } from "../../types/dashBoardTypes";
const { useToken } = theme;

type Props = {
    data?: ClaimByStatus[];
};

const STATUS_ORDER = [
    "Created",
    "AwaitingApproval",
    "Approved",
    "Rejected",
    "Paid",
];

export default function PieChartWithCustomizedLabel({ data }: Props) {
    const { token } = useToken();
    const { t } = useTranslation();

    const statusLabels: Record<string, string> = {
        Created: t('created_label'),
        AwaitingApproval: t('awaiting_approval_label'),
        Approved: t('approved_label'),
        Rejected: t('rejected_label'),
        Paid: t('paid_label'),
    };

    // Match dashboard palette (teal/cyan tones used in metric cards)
    const colors = [
        '#14B8A6', // Created
        '#0891B2', // Awaiting Approval
        '#06B6D4', // Approved
        token.colorError, // Rejected
        '#0D9488', // Paid
    ];

    if (!data || data.length === 0) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }


    const chartData = STATUS_ORDER
        .map(status =>
            data.find(d => d.status === status)
        )
        .filter(Boolean)
        .map(item => ({
            name: statusLabels[String(item!.status)] ?? String(item!.status),
            value: item!.total,
        }));

    const total = chartData.reduce((s, i) => s + i.value, 0);

    if (!total) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    const renderLabel = ({
        cx,
        cy,
        midAngle,
        outerRadius,
        value,
        payload,
    }: PieLabelRenderProps) => {
        if (!value || !total || midAngle === undefined || !outerRadius || cx === undefined || cy === undefined) return null;

        const radius = outerRadius + 34; // place labels farther to avoid clipping
        const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
        const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);
        const percent = Math.round((value / total) * 100);
        const name = payload?.name ?? '';

        // Find the color for this label (by index)
        const colorIndex = payload && payload.payload && typeof payload.payload.index === 'number'
            ? payload.payload.index
            : chartData.findIndex(d => d.name === name);
        const labelColor = colors[colorIndex % colors.length] || token.colorText;

        return (
            <text
                x={x}
                y={y}
                fill={labelColor}
                textAnchor={x > cx ? "start" : "end"}
                dominantBaseline="central"
                style={{ fontSize: 11, fontWeight: 600 }}
            >
                <tspan x={x} dy={-2}>{name}</tspan>
                <tspan x={x} dy={14}>{percent}% ({value})</tspan>
            </text>
        );
    };

    return (
        <div style={{ width: "100%" }}>

            {/* CHART (CENTERED) */}
            <div style={{ display: "flex", justifyContent: "center" }}>
                <PieChart width={400} height={360}>
                    <Pie
                        data={chartData}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        innerRadius={70}
                        outerRadius={90}
                        labelLine
                        label={renderLabel}
                    >
                        {chartData.map((_, index) => (
                            <Cell key={index} fill={colors[index % colors.length]} />
                        ))}
                    </Pie>

                    {/* Center Total */}
                    <text
                        x="50%"
                        y="50%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{ fontSize: 20, fontWeight: 700, fill: token.colorText }}
                    >
                        {total}
                    </text>
                    <text
                        x="50%"
                        y="58%"
                        textAnchor="middle"
                        style={{ fontSize: 12, fill: token.colorTextSecondary }}
                    >
                        {t('total_claims_label')}
                    </text>
                </PieChart>
            </div>

            {/* Legend removed as requested; percentages shown on chart labels */}

        </div>
    );
}